// ChatManager: handles text messages, typing, emojis, and file/link attachments over PeerJS data channels
// React-friendly, event-driven, no direct DOM usage.

class Emitter {
  constructor() { this._e = {}; }
  on(ev, fn) { (this._e[ev] ||= new Set()).add(fn); return () => this.off(ev, fn); }
  off(ev, fn) { const s = this._e[ev]; if (!s) return; s.delete(fn); }
  emit(ev, payload) { const s = this._e[ev]; if (!s) return; for (const fn of Array.from(s)) try { fn(payload); } catch (_) {} }
}

export const MESSAGE_TYPE = {
  USER_UPDATE: 'user',
  FILE: 'file',
  FILE_ACCEPT: 'file-accept',
  TYPING: 'typing',
  READ_RECEIPT: 'receipt',
  LINK: 'link',
  TEXT: 'text',
  MEDIA: 'media',
  ASK_REFRESH: 'ask-refresh',
  DO_REFRESH: 'do-refresh',
  RECONNECT_OFFER: 'reconnect-offer',
  FILE_RESUME: 'file-resume'
};
export class ChatManager {
  constructor({ connectionManager, currentUser }) {
    if (!connectionManager) throw new Error('ChatManager requires a connectionManager');
    this.conn = connectionManager;
    this.user = currentUser || { userName: 'Anonymous' };
    this.emitter = new Emitter();
    this.emojiList = this.#buildEmojiList();
    this._sendProgress = {}; // id -> { sent, total, name }
    this._recvProgress = {}; // id -> { received, total, name }
    this._activeSends = {}; // id+peerId -> { id, peerId, file, sent, seq, total, name, type, paused }
    this._pausedSends = {}; // id+peerId -> { id, peerId, file, sent, seq, total, name, type }
    this._resumeHandlers = {}; // id+peerId -> cleanup function

    // Wire incoming data channel messages
    this._off = [];
    this._off.push(
      this.conn.on('dc:data', ({ peerId, label, data }) => {
        if (label !== 'text' && label !== 'file') return;
        try { this.#handleIncoming(peerId, label, data); } catch (_) {}
      })
    );

    // Track data channel close events to pause file transfers
    this._off.push(
      this.conn.on('dc:close', ({ peerId, label }) => {
        if (label === 'file') {
          this.#handleFileChannelClose(peerId);
        }
      })
    );

    // On peer reconnect, resume any paused file transfers
    this._off.push(
      this.conn.on('peer:connected', ({ peerId }) => {
        this.#resumePausedSends(peerId);
      })
    );

    // On full peer re-init, mark all sends as paused (they'll resume when data channels re-establish)
    this._off.push(
      this.conn.on('peer:reinitialized', () => {
        this.#handlePeerReinit();
      })
    );
  }

  dispose() { this._off.forEach(fn => { try { fn(); } catch(_) {} }); this._off = []; }

  on(ev, fn) { return this.emitter.on(ev, fn); }
  emit(ev, payload) { this.emitter.emit(ev, payload); }

  setCurrentUser(user) { this.user = user || this.user; }

  // Accept a pending file offer from a specific peer (sender)
  acceptFile(toPeerId, id) {
    if (!toPeerId || !id) return;
    const payload = this.#msg(MESSAGE_TYPE.FILE_ACCEPT, { id });
    this.#sendToPeer(toPeerId, 'text', payload);
  }

  // --------- Send APIs ---------
  sendText(message) {
    if (!message || typeof message !== 'string') return;
    const payload = this.#msg(MESSAGE_TYPE.TEXT, message);
    console.log('[ChatManager] Sending text message:', payload);
    this.#broadcast('text', payload);
    this.emit('message:sent', { data: message, ...payload });
  }

  sendTyping(isTyping = true) {
    const payload = this.#msg(MESSAGE_TYPE.TYPING, { isTyping: !!isTyping });
    this.#broadcast('text', payload);
  }

  sendLink(url, meta = {}) {
    const payload = this.#msg(MESSAGE_TYPE.LINK, { url, ...meta });
    this.#broadcast('text', payload);
    this.emit('link:sent', payload);
  }

  async sendFile(file, extra = {}) {
    // Prepare identifiers and metadata
    const id = extra?.id || `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    // Metadata first (include id for progress correlation)
    const meta = this.#msg(MESSAGE_TYPE.FILE, { id, name: file.name, size: file.size, type: file.type, ...extra });
    this.#broadcast('text', meta);
    // Inform UI we're waiting for recipient acceptance
    this.emit('file:send:waiting', { id, name: file.name, size: file.size, type: file.type });

    // For each acceptance, start an independent send to that peer
    const CHUNK_SIZE = 16384; // 16KB for smoother flow on variable networks

    const sendToPeer = async (peerId, resumeOffset = 0) => {
      const sendKey = `${id}:${peerId}`;
      try {
        let seq = 0;
        let sent = 0;

        // If resuming, skip to the correct position in the file
        if (resumeOffset > 0) {
          sent = resumeOffset;
          seq = Math.floor(resumeOffset / CHUNK_SIZE);
          console.log(`[ChatManager] Resuming file transfer to ${peerId} from offset ${resumeOffset} (seq ${seq})`);
          this.emit('file:send:resumed', { id, peerId, offset: resumeOffset, name: file.name, size: file.size });
        } else {
          this.emit('file:send:start', { id, peerId, name: file.name, size: file.size, type: file.type });
        }

        // Track this as an active send so we can pause/resume
        this._activeSends[sendKey] = { id, peerId, file, sent, seq, total: file.size, name: file.name, type: file.type, paused: false };

        // Create a fresh reader each time (can't reuse a released reader)
        const reader = file.stream().getReader();

        // If resuming, skip already-sent bytes
        if (resumeOffset > 0) {
          let skipped = 0;
          while (skipped < resumeOffset) {
            const { done, value } = await reader.read();
            if (done) break;
            skipped += value.byteLength;
          }
        }

        // Backpressure & keepalive setup
        const conns = this.conn.getConnections();
        const dc = conns[peerId]?.file; // PeerJS DataConnection
        const rtc = dc?.dataChannel; // RTCDataChannel
        if (rtc && typeof rtc.bufferedAmountLowThreshold === 'number') {
          rtc.bufferedAmountLowThreshold = 256 * 1024;
        }
        let lastKA = Date.now();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          for (let offset = 0; offset < value.length; offset += CHUNK_SIZE) {
            const sub = value.subarray(offset, Math.min(offset + CHUNK_SIZE, value.length));

            // Create proper packet with metadata and chunk
            const metadata = JSON.stringify({ id, seq, end: false });
            const metaBytes = new TextEncoder().encode(metadata);
            const metaLen = new Uint32Array([metaBytes.length]);

            // Combine: [4 bytes length][metadata JSON][chunk data]
            const packet = new Uint8Array(4 + metaBytes.length + sub.byteLength);
            packet.set(new Uint8Array(metaLen.buffer), 0);
            packet.set(metaBytes, 4);
            packet.set(sub, 4 + metaBytes.length);

            // Backpressure: if RTC channel queue is large, wait for it to drain
            if (rtc) {
              try {
                if (rtc.bufferedAmountLowThreshold && rtc.bufferedAmount > rtc.bufferedAmountLowThreshold) {
                  await new Promise((resolve) => {
                    const onLow = () => resolve();
                    rtc.addEventListener('bufferedamountlow', onLow, { once: true });
                  });
                }
              } catch (_) {}
            }

            // Check if this send was paused (data channel dropped)
            if (this._activeSends[sendKey]?.paused) {
              console.log(`[ChatManager] Send paused for ${sendKey}, stopping at offset ${sent}`);
              reader.releaseLock();
              return; // Stop sending; will resume on reconnect
            }

            this.#sendToPeer(peerId, 'file', packet.buffer);
            sent += sub.byteLength;
            this._activeSends[sendKey].sent = sent;
            this._activeSends[sendKey].seq = seq;

            if (seq % 10 === 0) {
              console.log(`[ChatManager] Sent chunk ${seq}, ${sent}/${file.size} bytes (${Math.round((sent/file.size)*100)}%)`);
            }
            this.emit('file:send:progress', { id, peerId, sent, total: file.size, percent: file.size ? Math.min(100, Math.round((sent/file.size)*100)) : 0 });
            seq += 1;
            // Periodic keepalive to keep NATs happy during long sends
            if (Date.now() - lastKA > 5000) {
              try { this.#sendToPeer(peerId, 'file', { id, seq: -1, end: false, ka: true, ts: Date.now() }); } catch(_) {}
              lastKA = Date.now();
            }
          }
        }
        // Send end packet
        const endMeta = JSON.stringify({ id, seq, end: true });
        const endMetaBytes = new TextEncoder().encode(endMeta);
        const endMetaLen = new Uint32Array([endMetaBytes.length]);
        const endPacket = new Uint8Array(4 + endMetaBytes.length);
        endPacket.set(new Uint8Array(endMetaLen.buffer), 0);
        endPacket.set(endMetaBytes, 4);
        this.#sendToPeer(peerId, 'file', endPacket.buffer);
        this.emit('file:sent', { id, peerId, name: file.name, size: file.size, type: file.type });
        this.emit('file:send:completed', { id, peerId });
        // Clean up tracking
        delete this._activeSends[sendKey];
        delete this._pausedSends[sendKey];
      } catch (e) {
        console.error('[ChatManager] Error sending file to peer', peerId, e);
        this.emit('file:send:error', { id, peerId, error: e });
        // On error, move to paused state for potential resume
        if (this._activeSends[sendKey]) {
          this._pausedSends[sendKey] = { ...this._activeSends[sendKey], paused: true };
          delete this._activeSends[sendKey];
        }
      }
    };

    // Listen for acceptances and start transfers per acceptance
    const handler = ({ peerId, id: accId }) => { 
      if (accId === id) {
        console.log('[ChatManager] File accepted by', peerId, 'starting transfer');
        sendToPeer(peerId); 
      } 
    };
    const off = this.on('file:accept', handler);
    
    // Keep listener active for 60 seconds, then cleanup
    setTimeout(() => {
      off();
      console.log('[ChatManager] File transfer acceptance window closed for', id);
    }, 60000);
  }

  // --------- Internals ---------
  #broadcast(label, payload, retryPeers = null) {
    try {
      const conns = this.conn.getConnections();
      const failed = [];
      const targets = retryPeers || Object.keys(conns);
      
      if (!retryPeers) {
        console.log(`[ChatManager] Broadcasting to ${label} channel to ${targets.length} peers:`, payload);
      }
      
      targets.forEach(peerId => {
        const channels = conns[peerId];
        const dc = channels?.[label];
        if (!dc) {
          console.warn(`[ChatManager] No ${label} channel for peer ${peerId}`);
          try { this.conn.connectToUser?.(peerId); } catch(_) {}
          failed.push(peerId);
          return;
        }
        const ready = dc.open && (dc.dataChannel?.readyState ? dc.dataChannel.readyState === 'open' : true);
        if (!ready) {
          console.warn(`[ChatManager] ${label} channel to ${peerId} is not open`);
          try { this.conn.connectToUser?.(peerId); } catch(_) {}
          failed.push(peerId);
          return;
        }
        
        try {
          dc.send(payload);
        } catch (error) {
          console.error(`[ChatManager] Error sending to ${peerId} (${label}):`, error);
          failed.push(peerId);
        }
      });
      
      // Enhanced retry logic: retry up to 3 times with exponential backoff
      if (failed.length > 0 && !retryPeers) {
        console.warn(`[ChatManager] Scheduling retry for ${failed.length} peer(s):`, failed);
        this.#retryBroadcast(label, payload, failed, 1);
      } else if (failed.length > 0 && retryPeers) {
        console.error(`[ChatManager] Final delivery failed for ${failed.length} peer(s):`, failed);
        this.emit('message:delivery:failed', { peers: failed, payload });
      }
    } catch (error) {
      console.error('[ChatManager] Error in broadcast:', error);
    }
  }
  #retryBroadcast(label, payload, peers, attempt) {
    const maxAttempts = 3;
    if (attempt > maxAttempts) {
      console.error(`[ChatManager] Max retry attempts (${maxAttempts}) reached for peers:`, peers);
      this.emit('message:delivery:failed', { peers, payload });
      return;
    }
    
    // Exponential backoff: 1.5s, 3s, 6s
    const delay = 1500 * Math.pow(2, attempt - 1);
    console.log(`[ChatManager] Retry attempt ${attempt}/${maxAttempts} in ${delay}ms for peers:`, peers);
    
    setTimeout(() => {
      try {
        const conns = this.conn.getConnections();
        const stillFailed = [];
        
        peers.forEach(peerId => {
          const channels = conns[peerId];
          const dc = channels?.[label];
          
          // Check if channel is now ready
          const ready = dc?.open && (dc.dataChannel?.readyState ? dc.dataChannel.readyState === 'open' : true);
          
          if (!ready) {
            console.warn(`[ChatManager] Retry ${attempt}: Channel still not ready for ${peerId}`);
            stillFailed.push(peerId);
            return;
          }
          
          try {
            dc.send(payload);
            console.log(`[ChatManager] Retry ${attempt}: Successfully sent to ${peerId}`);
          } catch (error) {
            console.error(`[ChatManager] Retry ${attempt}: Error sending to ${peerId}:`, error);
            stillFailed.push(peerId);
          }
        });
        
        // If still have failures, retry again
        if (stillFailed.length > 0) {
          this.#retryBroadcast(label, payload, stillFailed, attempt + 1);
        }
      } catch (error) {
        console.error(`[ChatManager] Error in retry attempt ${attempt}:`, error);
      }
    }, delay);
  }
  #sendToPeer(peerId, label, payload) {
    try {
      const conns = this.conn.getConnections();
      const ch = conns[peerId] && conns[peerId][label];
      if (!ch) { 
        console.warn(`[ChatManager] No ${label} channel for peer ${peerId}`); 
        console.log('[ChatManager] Available channels:', Object.keys(conns[peerId] || {}));
        return; 
      }
      if (!ch.open) { 
        console.warn(`[ChatManager] ${label} channel to ${peerId} is not open`); 
        return; 
      }
      ch.send(payload);
    } catch (e) {
      console.error(`[ChatManager] Error sending to peer ${peerId} on ${label}:`, e);
    }
  }
  #handleIncoming(peerId, label, data) {
    console.log(`[ChatManager] Received ${label} data from ${peerId}:`, data);
    
    // file chunks are binary data; text is object messages
    if (label === 'file') {
      // Parse binary format: [4 bytes length][metadata JSON][chunk data]
      // Handle both ArrayBuffer and Uint8Array (PeerJS may send either)
      let view;
      let buffer;
      
      if (data instanceof ArrayBuffer) {
        view = new Uint8Array(data);
        buffer = data;
      } else if (data instanceof Uint8Array) {
        view = data;
        buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
      } else {
        console.error('[ChatManager] Unexpected data type for file chunk:', typeof data);
        return;
      }
      
      const metaLen = new Uint32Array(buffer.slice(0, 4))[0];
      const metaBytes = view.slice(4, 4 + metaLen);
      const metadata = JSON.parse(new TextDecoder().decode(metaBytes));
      const chunk = view.slice(4 + metaLen);
      
      const { id, seq, end, ka } = metadata;
      
      // Skip keepalive packets
      if (ka) return;
      
      if (!end) {
        const len = chunk.byteLength;
        console.log(`[ChatManager] File chunk received - id: ${id}, seq: ${seq}, size: ${len}`);
        
        // progress bookkeeping (total will be set when metadata arrives)
        const prog = this._recvProgress[id] || (this._recvProgress[id] = { received: 0, total: 0, name: '' });
        prog.received += len;
        
        // Convert to ArrayBuffer for consistency
        const chunkBuffer = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
        this.emit('file:chunk', { peerId, id, seq, chunk: chunkBuffer });
        
        const { received, total } = this._recvProgress[id];
        if (total > 0) {
          console.log(`[ChatManager] File progress - ${received}/${total} bytes (${Math.round((received/total)*100)}%)`);
          this.emit('file:receive:progress', { id, received, total, percent: Math.min(100, Math.round((received/total)*100)) });
        }
      } else {
        console.log(`[ChatManager] File transfer completed for id: ${id}`);
        this.emit('file:received', { peerId, id });
        this.emit('file:receive:completed', { id });
        delete this._recvProgress[id];
      }
      return;
    }
    // text channel payloads
    if (typeof data !== 'object' || !data.type) {
      // Back-compat: treat plain strings as text messages
      if (typeof data === 'string') {
        const payload = this.#msg(MESSAGE_TYPE.TEXT, data, { from: peerId });
        this.emit('message:received', payload);
      }
      return;
    }
    const type = data.type;
    switch (type) {
      case MESSAGE_TYPE.TEXT:
        this.emit('message:received', data);
        break;
      case MESSAGE_TYPE.TYPING:
        this.emit('typing', { peerId, data });
        break;
      case MESSAGE_TYPE.FILE:
        // file meta contains: { id, name, size, type }
        if (data && data.data && data.data.id) {
          const { id, name, size } = data.data;
          this._recvProgress[id] = { received: 0, total: size || 0, name: name || '' };
          this.emit('file:receive:start', { id, name, size });
        }
        this.emit('file:meta', { peerId, data });
        break;
      case MESSAGE_TYPE.FILE_ACCEPT:
        if (data && data.data && data.data.id) {
          this.emit('file:accept', { peerId, id: data.data.id });
        }
        break;
      case MESSAGE_TYPE.LINK:
        this.emit('link:received', { peerId, data });
        break;
      case MESSAGE_TYPE.RECONNECT_OFFER:
        this.emit('reconnect:offer', { peerId, have: data?.data?.have || {} });
        break;
      case MESSAGE_TYPE.FILE_RESUME:
        // Receiver is requesting resume from a specific offset, or sender is telling receiver to resume
        if (data && data.data) {
          const { id: fileId, offset, name: fileName, size: fileSize, resume: isResumeReq } = data.data;
          if (isResumeReq) {
            // Receiver requests resume from offset — sender should restart from that offset
            console.log(`[ChatManager] Resume request from ${peerId} for file ${fileId} at offset ${offset}`);
            const sendKey = `${fileId}:${peerId}`;
            const paused = this._pausedSends[sendKey];
            if (paused && paused.file) {
              // Restart the send from the requested offset
              delete this._pausedSends[sendKey];
              this.#sendToPeerForResume(fileId, peerId, paused.file, offset, paused.name, paused.type, paused.total);
            } else {
              console.warn(`[ChatManager] No paused send found for ${sendKey}, cannot resume`);
            }
          } else {
            // Sender is notifying us that they will resume from offset — receiver should prepare
            console.log(`[ChatManager] Sender ${peerId} resuming file ${fileId} from offset ${offset}`);
            // Reset receive progress to the resume offset
            this._recvProgress[fileId] = { received: offset, total: fileSize || 0, name: fileName || '' };
            this.emit('file:receive:resumed', { id: fileId, peerId, offset, name: fileName, size: fileSize });
          }
        }
        break;
      case MESSAGE_TYPE.MEDIA:
        this.emit('media:event', { peerId, data });
        break;
      default:
        this.emit('unknown', { peerId, data });
    }
  }

  #msg(type, data, overrides = {}) {
    const user = this.user || {};
    return {
      type,
      from: overrides.from || (user.peerId || null),
      user: { peerId: user.peerId, userName: user.userName, initial: user.initial, color: user.color, status: user.status },
      ts: Date.now(),
      data
    };
  }

  #buildEmojiList() {
    // Minimal curated set; UI can supply full picker if needed
    return ['😀','😁','😂','🤣','😊','😍','😎','😢','😡','👍','👎','🙏','👏','🎉','💯'];
  }

  // --- File transfer resume helpers ---

  // Re-send a file from a specific offset (used by resume protocol)
  #sendToPeerForResume(id, peerId, file, resumeOffset, name, type, total) {
    const CHUNK_SIZE = 16384;
    const sendKey = `${id}:${peerId}`;
    const doSend = async () => {
      try {
        let seq = Math.floor(resumeOffset / CHUNK_SIZE);
        let sent = resumeOffset;
        this.emit('file:send:resumed', { id, peerId, offset: resumeOffset, name, size: total });
        // Notify receiver that we're resuming from this offset
        const resumeNotify = this.#msg(MESSAGE_TYPE.FILE_RESUME, { id, offset: resumeOffset, name, size: total, type, resume: false });
        this.#sendToPeer(peerId, 'text', resumeNotify);

        this._activeSends[sendKey] = { id, peerId, file, sent, seq, total, name, type, paused: false };
        const reader = file.stream().getReader();
        // Skip already-sent bytes
        let skipped = 0;
        while (skipped < resumeOffset) {
          const { done, value } = await reader.read();
          if (done) break;
          skipped += value.byteLength;
        }
        const conns = this.conn.getConnections();
        const dc = conns[peerId]?.file;
        const rtc = dc?.dataChannel;
        if (rtc && typeof rtc.bufferedAmountLowThreshold === 'number') {
          rtc.bufferedAmountLowThreshold = 256 * 1024;
        }
        let lastKA = Date.now();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          for (let offset = 0; offset < value.length; offset += CHUNK_SIZE) {
            const sub = value.subarray(offset, Math.min(offset + CHUNK_SIZE, value.length));
            const metadata = JSON.stringify({ id, seq, end: false });
            const metaBytes = new TextEncoder().encode(metadata);
            const metaLen = new Uint32Array([metaBytes.length]);
            const packet = new Uint8Array(4 + metaBytes.length + sub.byteLength);
            packet.set(new Uint8Array(metaLen.buffer), 0);
            packet.set(metaBytes, 4);
            packet.set(sub, 4 + metaBytes.length);
            if (rtc) {
              try {
                if (rtc.bufferedAmountLowThreshold && rtc.bufferedAmount > rtc.bufferedAmountLowThreshold) {
                  await new Promise((resolve) => {
                    const onLow = () => resolve();
                    rtc.addEventListener('bufferedamountlow', onLow, { once: true });
                  });
                }
              } catch (_) {}
            }
            if (this._activeSends[sendKey]?.paused) {
              console.log(`[ChatManager] Resumed send paused again for ${sendKey} at offset ${sent}`);
              reader.releaseLock();
              return;
            }
            this.#sendToPeer(peerId, 'file', packet.buffer);
            sent += sub.byteLength;
            this._activeSends[sendKey].sent = sent;
            this._activeSends[sendKey].seq = seq;
            if (seq % 10 === 0) {
              console.log(`[ChatManager] Resumed send chunk ${seq}, ${sent}/${total} bytes (${Math.round((sent/total)*100)}%)`);
            }
            this.emit('file:send:progress', { id, peerId, sent, total, percent: total ? Math.min(100, Math.round((sent/total)*100)) : 0 });
            seq += 1;
            if (Date.now() - lastKA > 5000) {
              try { this.#sendToPeer(peerId, 'file', { id, seq: -1, end: false, ka: true, ts: Date.now() }); } catch(_) {}
              lastKA = Date.now();
            }
          }
        }
        // Send end packet
        const endMeta = JSON.stringify({ id, seq, end: true });
        const endMetaBytes = new TextEncoder().encode(endMeta);
        const endMetaLen = new Uint32Array([endMetaBytes.length]);
        const endPacket = new Uint8Array(4 + endMetaBytes.length);
        endPacket.set(new Uint8Array(endMetaLen.buffer), 0);
        endPacket.set(endMetaBytes, 4);
        this.#sendToPeer(peerId, 'file', endPacket.buffer);
        this.emit('file:sent', { id, peerId, name, size: total, type });
        this.emit('file:send:completed', { id, peerId });
        delete this._activeSends[sendKey];
        delete this._pausedSends[sendKey];
      } catch (e) {
        console.error('[ChatManager] Error in resumed file send to', peerId, e);
        this.emit('file:send:error', { id, peerId, error: e });
        if (this._activeSends[sendKey]) {
          this._pausedSends[sendKey] = { ...this._activeSends[sendKey], paused: true };
          delete this._activeSends[sendKey];
        }
      }
    };
    doSend();
  }

  // Called when a file data channel closes — pause all active sends to that peer
  #handleFileChannelClose(peerId) {
    let pausedAny = false;
    for (const [sendKey, send] of Object.entries(this._activeSends)) {
      if (send.peerId === peerId && !send.paused) {
        send.paused = true;
        this._pausedSends[sendKey] = { ...send };
        delete this._activeSends[sendKey];
        pausedAny = true;
        console.log(`[ChatManager] File transfer paused for ${sendKey} at offset ${send.sent}`);
        this.emit('file:send:paused', { id: send.id, peerId, offset: send.sent, name: send.name, size: send.total });
      }
    }
    if (pausedAny) {
      console.log(`[ChatManager] Paused all file sends to ${peerId} due to channel close`);
    }
  }

  // Called when a peer reconnects — resume any paused file sends to that peer
  #resumePausedSends(peerId) {
    for (const [sendKey, paused] of Object.entries(this._pausedSends)) {
      if (paused.peerId === peerId && paused.file) {
        console.log(`[ChatManager] Resuming paused file send to ${peerId} for ${sendKey} from offset ${paused.sent}`);
        // Send a resume request to the receiver so they know to expect data from this offset
        const resumeReq = this.#msg(MESSAGE_TYPE.FILE_RESUME, { id: paused.id, offset: paused.sent, name: paused.name, size: paused.total, type: paused.type, resume: true });
        // Wait a bit for the data channel to fully establish
        setTimeout(() => {
          this.#sendToPeer(peerId, 'text', resumeReq);
          // Also start sending from the paused offset after a short delay
          this.#sendToPeerForResume(paused.id, peerId, paused.file, paused.sent, paused.name, paused.type, paused.total);
          delete this._pausedSends[sendKey];
        }, 1500);
      }
    }
  }

  // Called when our own peer is fully re-initialized — all data channels are dead
  #handlePeerReinit() {
    // Move all active sends to paused state
    for (const [sendKey, send] of Object.entries(this._activeSends)) {
      send.paused = true;
      this._pausedSends[sendKey] = { ...send };
      delete this._activeSends[sendKey];
      console.log(`[ChatManager] File transfer paused (peer re-init) for ${sendKey} at offset ${send.sent}`);
      this.emit('file:send:paused', { id: send.id, peerId: send.peerId, offset: send.sent, name: send.name, size: send.total });
    }
    // They will resume when peer:connected events fire for each peer
  }

  // Public API: receiver can request resume from a specific offset (e.g. after partial download)
  requestFileResume(toPeerId, id, offset) {
    if (!toPeerId || !id) return;
    const payload = this.#msg(MESSAGE_TYPE.FILE_RESUME, { id, offset, resume: true });
    this.#sendToPeer(toPeerId, 'text', payload);
    console.log(`[ChatManager] Sent file resume request to ${toPeerId} for ${id} at offset ${offset}`);
  }
}
